import {
    Component,
    DestroyRef,
    effect,
    inject,
    Input,
    OnInit,
    signal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlContainer,
    FormBuilder,
    FormControl,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { startWith } from 'rxjs';
import { ElementWithOptionsModel } from '../../../models/clientsform/element-with-options';
import { ConstantsService } from '../../../services/constants.service';
import { LocalizationService } from '../../../services/localization.service';
import { TranslationComponent } from '../../translation/translation.component';

@Component({
    selector: 'apcam-checkbox-element',
    imports: [MatCheckboxModule, ReactiveFormsModule, TranslationComponent],
    templateUrl: './checkbox-element.component.html',
    styleUrl: './checkbox-element.component.scss',
    viewProviders: [
        {
            provide: ControlContainer,
            useFactory: () => inject(ControlContainer, { skipSelf: true }),
        },
    ],
})
export class CheckboxElementComponent implements OnInit {
    // constants needed for 'encoding' and decoding a checkbox values to/from strings
    public static readonly PREFIX = ConstantsService.CHECKBOX_PREFIX;
    public static readonly SEPARATOR = ConstantsService.CHECKBOX_SEPARATOR;

    @Input({ required: true })
    public formcontrolName!: string;

    @Input({ required: true })
    public inputConfig!: ElementWithOptionsModel;

    @Input()
    public readonlyState: Signal<boolean> = signal(false);

    /**
     * Signal for 'dirty' form group to the parent - required since parent form does not detect checkboxes as dirty
     * automatically due to the implementation.
     */
    @Input()
    public checkboxDirty: WritableSignal<boolean> = signal(false);

    private currentValue: string = '';

    private readonly controlContainer = inject(ControlContainer);
    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(FormBuilder);
    private readonly localizationService = inject(LocalizationService);

    protected get parentFormGroup() {
        return this.controlContainer.control;
    }

    protected get defaultLang() {
        return this.localizationService.defaultLanguage;
    }

    protected readonly checkboxFormGroup = this.formBuilder.group({});

    constructor() {
        effect(() => {
            // React to changes in readonlyState signal
            if (this.readonlyState()) {
                this.checkboxFormGroup.disable();
            } else {
                this.checkboxFormGroup.enable();
            }
        });
    }

    ngOnInit(): void {
        // 1. Create an inner formgroup for the checkboxes (individual options)
        this.inputConfig.options.forEach((option) => {
            this.checkboxFormGroup.addControl(
                option.label.translations[this.defaultLang],
                new FormControl(false)
            );
        });
        this.checkboxFormGroup.disable();

        // 2. This ugly solution tracks changes in the [checkbox label] control's value. When it is changed, elements
        // (checkboxes) in this object's checkboxFormGroup are updated (this is useful for initial value loading only).
        // Manages outer form group value change => inner form group value change transitions
        const parentControl = this.parentFormGroup?.get([this.formcontrolName]);
        parentControl?.valueChanges
            .pipe(
                startWith(parentControl.value),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((val: string) => {
                if (val?.startsWith(CheckboxElementComponent.PREFIX)) {
                    const options = val
                        .substring(CheckboxElementComponent.PREFIX.length)
                        .split(CheckboxElementComponent.SEPARATOR);
                    options.forEach((option) => {
                        this.checkboxFormGroup.get([option])?.setValue(true);
                    });
                } else {
                    // works for when the form is in edit mode and returns back to readonly and previous
                    // value did not exist (not previously set) => reset all checkboxes
                    this.checkboxFormGroup.reset();
                }
                this.currentValue = val;
            });

        // 3. Upon inner formgroup change, write to the outer formGroup
        this.checkboxFormGroup.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                const newValue =
                    CheckboxElementComponent.PREFIX +
                    Object.entries(result)
                        .filter(([_, value]) => value) // Get the options which are checked
                        .map(([key, _]) => key)
                        .join(CheckboxElementComponent.SEPARATOR);
                if (newValue === this.currentValue) {
                    // prevents setValue => getValue endless recursion
                    return;
                }
                this.currentValue = newValue;
                parentControl?.setValue(newValue);
                this.checkboxDirty.set(true);
            });
    }
}
